import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, AlertTriangle, Scale } from "lucide-react";

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-background animate-fade-in" data-testid="terms-page">
      {/* Header */}
      <div className="bg-zinc-950 border-b border-zinc-800 py-16">
        <div className="container mx-auto px-4">
          <Link to="/" className="inline-flex items-center text-zinc-400 hover:text-white transition-colors mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-white uppercase tracking-tight" data-testid="page-title">
            Terms of Service
          </h1>
          <p className="text-zinc-400 mt-4">Last updated: January 2026</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Important Notice */}
          <div className="bg-secondary/10 border border-secondary p-6 mb-12 flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-secondary flex-shrink-0 mt-1" />
            <div>
              <p className="text-white font-semibold">Important:</p>
              <p className="text-zinc-300">
                By placing an order on brendollysautoparts.com, you agree to be bound by these Terms of Service. 
                Please read them carefully before making a purchase.
              </p>
            </div>
          </div>

          {/* Policy Content */}
          <div className="prose prose-invert max-w-none space-y-8">
            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">1. Acceptance of Terms</h2>
              <p className="text-zinc-400">
                Welcome to Brendolly's Auto Parts ("Company," "we," "us," or "our"). These Terms of Service ("Terms") 
                govern your use of our website brendollysautoparts.com (the "Site") and the purchase of products from us.
              </p>
              <p className="text-zinc-400 mt-4">
                By accessing our Site or placing an order, you agree to be bound by these Terms. If you do not agree to 
                all of these Terms, you may not access the Site or purchase products from us.
              </p>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">2. Product Listings & Availability</h2>
              <ul className="text-zinc-400 space-y-3">
                <li>
                  <strong className="text-white">Accuracy:</strong> We strive to ensure all product descriptions, images, 
                  specifications, and pricing are accurate. However, we do not warrant that product descriptions or other 
                  content is error-free, complete, or current.
                </li>
                <li>
                  <strong className="text-white">Availability:</strong> All products are subject to availability. We reserve 
                  the right to discontinue any product at any time without notice.
                </li>
                <li>
                  <strong className="text-white">Quantity Limits:</strong> We reserve the right to limit quantities of any 
                  products or services that we offer.
                </li>
              </ul>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">3. Pricing & Payment</h2>
              <div className="space-y-4">
                <div className="bg-zinc-950 p-4 border-l-4 border-primary">
                  <p className="text-white font-semibold">Price Changes</p>
                  <p className="text-zinc-400 mt-1">
                    Prices for our products are subject to change without prior notice. However, once you complete 
                    your purchase, your order will be honored at the price displayed at checkout.
                  </p>
                </div>
                <ul className="text-zinc-400 space-y-3">
                  <li>
                    <strong className="text-white">Payment Methods:</strong> We accept major credit cards (Visa, MasterCard, 
                    American Express, Discover), PayPal, and other payment methods displayed at checkout.
                  </li>
                  <li>
                    <strong className="text-white">Sales Tax:</strong> Massachusetts sales tax (6.25%) will be applied to all 
                    orders shipped to Massachusetts addresses, as required by law.
                  </li>
                  <li>
                    <strong className="text-white">Authorization:</strong> By providing payment information, you represent that 
                    you are authorized to use the payment method and authorize us to charge the total amount of your order.
                  </li>
                </ul>
              </div>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">4. Order Acceptance</h2>
              <p className="text-zinc-400">
                Your receipt of an order confirmation does not constitute our acceptance of your order. We reserve the right 
                to refuse or cancel any order for any reason, including but not limited to:
              </p>
              <ul className="list-disc list-inside text-zinc-400 mt-4 space-y-2">
                <li>Product availability issues</li>
                <li>Errors in product or pricing information</li>
                <li>Suspected fraudulent transactions</li>
                <li>Violations of these Terms of Service</li>
              </ul>
              <p className="text-zinc-400 mt-4">
                If we cancel your order after payment, we will issue a full refund to your original payment method.
              </p>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">5. User Accounts</h2>
              <ul className="text-zinc-400 space-y-3">
                <li>
                  <strong className="text-white">Account Creation:</strong> You may create an account to enjoy features like 
                  order tracking, faster checkout, and wishlists. You are responsible for maintaining the confidentiality 
                  of your account credentials.
                </li>
                <li>
                  <strong className="text-white">Accuracy:</strong> You agree to provide accurate, current, and complete 
                  information during registration and to update such information to keep it accurate.
                </li>
                <li>
                  <strong className="text-white">Security:</strong> You are responsible for all activities under your account. 
                  Notify us immediately if you suspect unauthorized use.
                </li>
              </ul>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">6. Intellectual Property</h2>
              <p className="text-zinc-400">
                All content on this Site, including text, graphics, logos, images, and software, is the property of 
                Brendolly's Auto Parts or its content suppliers and is protected by United States and international 
                copyright laws. You may not reproduce, distribute, modify, or create derivative works without our 
                prior written consent.
              </p>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">7. Product Warranty</h2>
              <p className="text-zinc-400">
                Products sold by Brendolly's Auto Parts carry the manufacturer's warranty, if any. We do not provide 
                additional warranties unless explicitly stated. We are not responsible for any damages resulting from 
                improper installation or use of products.
              </p>
              <p className="text-zinc-400 mt-4">
                <strong className="text-white">Disclaimer:</strong> Products are sold "as is" to the extent permitted by law. 
                We disclaim all warranties, express or implied, including merchantability and fitness for a particular purpose.
              </p>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">8. Limitation of Liability</h2>
              <p className="text-zinc-400">
                To the fullest extent permitted by law, Brendolly's Auto Parts shall not be liable for any indirect, 
                incidental, special, consequential, or punitive damages arising from your use of the Site or purchase 
                of products, including but not limited to vehicle damage, personal injury, or lost profits.
              </p>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">9. Governing Law</h2>
              <div className="flex items-start gap-4">
                <Scale className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                <p className="text-zinc-400">
                  These Terms shall be governed by and construed in accordance with the laws of the Commonwealth of Massachusetts, 
                  United States, without regard to its conflict of law provisions. Any disputes arising from these Terms shall 
                  be resolved in the state or federal courts located in Massachusetts.
                </p>
              </div>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">10. Changes to Terms</h2>
              <p className="text-zinc-400">
                We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting 
                to the Site. Your continued use of the Site after changes are posted constitutes your acceptance of the 
                modified Terms.
              </p>
            </section>

            <section className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="text-2xl font-heading text-white uppercase mb-4">11. Contact Information</h2>
              <p className="text-zinc-400 mb-4">
                If you have questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-zinc-950 p-4 border border-zinc-700">
                <p className="text-white font-semibold">Brendolly's Auto Parts</p>
                <p className="text-zinc-400">72 Chelsea Street</p>
                <p className="text-zinc-400">Everett, MA 02149</p>
                <p className="text-zinc-400 mt-2">Email: legal@brendollysautoparts.com</p>
                <p className="text-zinc-400">Phone: 781-870-7976</p>
              </div>
            </section>

            <section className="bg-primary/10 border border-primary p-8">
              <p className="text-zinc-300 text-center">
                By completing a purchase on our website, you acknowledge that you have read, understood, and agree to 
                be bound by these Terms of Service.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
